



































































































































import {
  defineComponent,
  ref,
  onMounted,
  Ref,
  watch,
  computed,
} from '@vue/composition-api';
import sl from '@/serviceLocator';
import { ThreadWithComments } from '@oare/types';

import CommentItem from './CommentItem.vue';
import ThreadListItem from './ThreadListItem.vue';

export default defineComponent({
  name: 'CommentItemDisplay',
  components: {
    CommentItem,
    ThreadListItem,
  },
  props: {
    item: {
      type: String,
      required: true,
    },
    route: {
      type: String,
      required: true,
    },
    uuid: {
      type: String,
      required: true,
    },
    value: {
      type: Boolean,
      default: true,
    },
    initialThreadUuid: {
      type: String,
      default: null,
    },
    showDictionary: {
      type: Boolean,
      default: false,
    },
    showEpigraphy: {
      type: Boolean,
      default: false,
    },
  },
  setup(props) {
    const itemUuid = ref('');
    const loading = ref(false);
    const threadsWithComments: Ref<ThreadWithComments[]> = ref([]);
    const selectedThreadIndex = ref(-1);

    const selectedThread = computed(() =>
      selectedThreadIndex.value >= 0
        ? threadsWithComments.value[selectedThreadIndex.value]
        : null
    );

    const userComment = ref('');
    const server = sl.get('serverProxy');
    const actions = sl.get('globalActions');
    const store = sl.get('store');

    const setSelectedThread = (index: number) => {
      selectedThreadIndex.value = index;
    };

    const updateThreadName = (index: number, newThreadName: string) => {
      threadsWithComments.value[index].name = newThreadName;
    };

    const getThreadsWithComments = async () => {
      try {
        if (props.uuid) {
          loading.value = true;
          const threads = await server.getThreadsWithCommentsByReferenceUuid(
            props.uuid
          );

          threadsWithComments.value = threads;
          if (selectedThreadIndex.value === -1 && threads.length > 0) {
            selectedThreadIndex.value = 0;
          }
        }
      } catch (err) {
        actions.showErrorSnackbar('Failed to get threads', err as Error);
      } finally {
        loading.value = false;
      }
    };

    const deleteComment = (index: number) => {
      threadsWithComments.value[selectedThreadIndex.value].comments[
        index
      ].deleted = true;
    };

    const validateComment = () => {
      if (!store.getters.user) {
        actions.showErrorSnackbar('Please log in before making a comment.');
        return false;
      }
      if (userComment.value.length > 1000) {
        actions.showErrorSnackbar(
          'Character count exceeded, please shorten the comment.'
        );
        return false;
      }

      if (userComment.value === '') {
        return false;
      }

      return true;
    };

    const insertComment = async () => {
      if (!validateComment()) {
        return;
      }

      loading.value = true;
      try {
        // If creating new thread
        let threadUuid: string;
        let isNewThread = false;
        const createThreadPayload = {
          referenceUuid: props.uuid,
          route: props.route,
        };
        if (!selectedThread.value) {
          threadUuid = await server.createThread(createThreadPayload);
          isNewThread = true;
        } else {
          threadUuid = selectedThread.value.uuid;
        }

        await server.insertComment({
          threadUuid,
          text: userComment.value,
        });

        await getThreadsWithComments();

        actions.showSnackbar(
          `Successfully added the comment for ${props.item}`
        );
        userComment.value = '';

        if (isNewThread) {
          selectedThreadIndex.value = threadsWithComments.value.length - 1;
        }
      } catch (err) {
        actions.showErrorSnackbar('Failed to insert the comment', err as Error);
      } finally {
        loading.value = false;
        userComment.value = '';
      }
    };

    onMounted(async () => {
      try {
        await getThreadsWithComments();

        if (props.initialThreadUuid) {
          selectedThreadIndex.value = threadsWithComments.value.findIndex(
            thread => thread.uuid === props.initialThreadUuid
          );
        }
      } catch (err) {
        actions.showErrorSnackbar('Failed to get threads', err as Error);
      }
    });

    const loggedInUser = computed(() =>
      store.getters.user ? store.getters.user : null
    );

    watch(
      selectedThread,
      () => {
        if (selectedThread.value) {
          itemUuid.value = selectedThread.value.route.substr(
            selectedThread.value.route.indexOf('/', 2) + 1
          );
        }
      },
      {
        immediate: false,
        deep: true,
      }
    );

    const dictionaryWordComponent = computed(() =>
      props.showDictionary
        ? () => import('@/views/DictionaryWord/index.vue')
        : null
    );

    const epigraphyComponent = computed(() =>
      props.showEpigraphy
        ? () => import('@/views/Texts/EpigraphyView/index.vue')
        : null
    );

    return {
      itemUuid,
      selectedThread,
      loading,
      loggedInUser,
      threadsWithComments,
      insertComment,
      userComment,
      dictionaryWordComponent,
      epigraphyComponent,
      selectedThreadIndex,
      console,
      setSelectedThread,
      getThreadsWithComments,
      deleteComment,
      updateThreadName,
    };
  },
});
